// src/components/Dashboard/Overview/OverviewPanel.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";
import vorxocIcon from "../../../Images/overviewLogo.svg";
import { Maximize2, X, Database, Server, Activity } from "lucide-react";

const PulseWindowMs = 900;

const normalizeRow = (row, index) => {
  const incidents =
    Number(row?.incidentCount ?? row?.incidents ?? row?.IncidentCount ?? row?.Incidents ?? 0) || 0;
  const alerts = Number(row?.alertCount ?? row?.alerts ?? row?.AlertCount ?? row?.Alerts ?? 0) || 0;
  const assets = Number(row?.assetCount ?? row?.assets ?? row?.AssetCount ?? row?.Assets ?? 0) || 0;
  const indicators =
    Number(row?.indicatorCount ?? row?.indicators ?? row?.IndicatorCount ?? row?.Indicators ?? 0) || 0;

  return {
    id: row?.id || row?.tableName || row?.table || `source-${index}`,
    tableName: row?.tableName || row?.table || `Table ${index + 1}`,
    workspaceName: row?.workspaceName || row?.workspace || row?.WorkspaceName || "Workspace",
    incidents,
    alerts,
    assets,
    indicators,
  };
};

const normalizeWorkspaceMetric = (row, index) => {
  const tableName = row?.tableName || row?.TableName || `Table ${index + 1}`;
  const workspaceName = row?.workspaceName || row?.WorkspaceName || `Workspace ${index + 1}`;
  const clientName = row?.clientName || row?.ClientName || "";
  const logCount = Number(row?.logCount ?? row?.LogCount ?? 0) || 0;
  const periodStart = row?.periodStart || row?.PeriodStart || "";
  const periodEnd = row?.periodEnd || row?.PeriodEnd || "";
  const periodType = row?.periodType || row?.PeriodType || "";
  const incidents = Number(row?.incidentCount ?? row?.IncidentCount ?? row?.incidents ?? 0) || 0;
  const alerts = Number(row?.alertCount ?? row?.AlertCount ?? row?.events ?? row?.Alerts ?? 0) || 0;
  const assets = Number(row?.assetCount ?? row?.AssetCount ?? 0) || 0;
  const indicators = Number(row?.indicatorCount ?? row?.IndicatorCount ?? row?.Indicators ?? 0) || 0;

  return {
    id: row?.metricId || row?.MetricId || `${workspaceName}-${tableName}-${index}`,
    tableName,
    workspaceName,
    clientName,
    logCount,
    periodStart,
    periodEnd,
    periodType,
    incidents,
    alerts,
    assets,
    indicators,
  };
};

// Custom Source Node (Left side - table/log sources)
const SourceNode = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className="flow-node-source"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "rgba(56, 189, 248, 0.8)",
          border: "none",
          width: 8,
          height: 8,
          right: -4,
        }}
      />
      <div className="flow-node-source__content">
        <Database size={14} className="flow-node-source__icon" />
        <span className="flow-node-source__label">{data.label}</span>
      </div>

      {showTooltip && data.details && (
        <div className="flow-node-source__tooltip">
          <div className="flow-node-source__tooltip-title">{data.label}</div>
          <div className="flow-node-source__tooltip-row">
            <span className="flow-node-source__tooltip-label">Workspace:</span>
            <span className="flow-node-source__tooltip-value">{data.details.workspaceName}</span>
          </div>
          {data.details.clientName && (
            <div className="flow-node-source__tooltip-row">
              <span className="flow-node-source__tooltip-label">Client:</span>
              <span className="flow-node-source__tooltip-value">{data.details.clientName}</span>
            </div>
          )}
          <div className="flow-node-source__tooltip-row">
            <span className="flow-node-source__tooltip-label">Log Count:</span>
            <span className="flow-node-source__tooltip-value">{data.details.logCount}</span>
          </div>
          <div className="flow-node-source__tooltip-row">
            <span className="flow-node-source__tooltip-label">Period:</span>
            <span className="flow-node-source__tooltip-value">{data.details.periodType}</span>
          </div>
          <div className="flow-node-source__tooltip-row">
            <span className="flow-node-source__tooltip-label">Start:</span>
            <span className="flow-node-source__tooltip-value">
              {formatDateTime(data.details.periodStart)}
            </span>
          </div>
          <div className="flow-node-source__tooltip-row">
            <span className="flow-node-source__tooltip-label">End:</span>
            <span className="flow-node-source__tooltip-value">
              {formatDateTime(data.details.periodEnd)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Workspace Node (Middle)
const WorkspaceNode = ({ data }) => {
  return (
    <div className="flow-node-workspace">
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "rgba(56, 189, 248, 0.8)",
          border: "none",
          width: 8,
          height: 8,
          left: -4,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "rgba(34, 197, 94, 0.8)",
          border: "none",
          width: 8,
          height: 8,
          right: -4,
        }}
      />
      <Server size={16} className="flow-node-workspace__icon" />
      <span className="flow-node-workspace__label">{data.label}</span>
    </div>
  );
};

// Custom VorXOC Node (Center with logo)
const VorxocNode = ({ data }) => {
  return (
    <div className="flow-node-vorxoc">
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "rgba(34, 197, 94, 0.8)",
          border: "none",
          width: 10,
          height: 10,
          left: -5,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "rgba(56, 189, 248, 0.8)",
          border: "none",
          width: 10,
          height: 10,
          right: -5,
        }}
      />
      <img src={vorxocIcon} alt="VorXOC" className="flow-node-vorxoc__logo" />
    </div>
  );
};

// Custom Stat Node (Right side)
const StatNode = ({ data }) => {
  const colorMap = {
    incidents: "#38bdf8",
    alerts: "#f97316",
    assets: "#22c55e",
    indicators: "#a855f7",
  };
  const borderColor = colorMap[data.statKey] || "rgba(148,163,184,0.4)";

  return (
    <div
      className="flow-node-stat"
      style={{
        border: `1px solid ${borderColor}`,
        boxShadow: `0 0 12px ${borderColor}`,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: borderColor,
          border: "none",
          width: 8,
          height: 8,
          left: -4,
        }}
      />
      <Activity size={14} className="flow-node-stat__icon" style={{ color: borderColor }} />
      <span className="flow-node-stat__label">{data.label}</span>
      <span className="flow-node-stat__value" style={{ color: borderColor }}>
        {data.value}
      </span>
    </div>
  );
};

const nodeTypes = {
  source: SourceNode,
  workspace: WorkspaceNode,
  vorxoc: VorxocNode,
  stat: StatNode,
};

// Center Modal (90% width, 80% height) Component
const CenterModal = ({ onClose, children }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="overview-flow-overlay" onClick={onClose}>
      <div className="overview-flow-modal" onClick={(e) => e.stopPropagation()}>
        <div className="overview-flow-modal__header">
          <div className="overview-flow-modal__spacer" />
          <button className="overview-flow-modal__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="overview-flow-modal__body">{children}</div>
      </div>
    </div>
  );
};

const FlowCanvas = ({ nodes, edges, onInit }) => {
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.12, minZoom: 0.5, maxZoom: 1.2 }}
      nodesDraggable={false}
      panOnDrag={true}
      zoomOnScroll={true}
      zoomOnPinch={true}
      zoomOnDoubleClick={false}
      minZoom={0.3}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
      style={{ background: "transparent" }}
      onInit={onInit}
    >
      <Background color="rgba(148,163,184,0.15)" gap={24} />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
};

export default function OverviewPanel({ range = "24h", clientId = null }) {
  const [flowData, setFlowData] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState({
    incidents: 0,
    alerts: 0,
    assets: 0,
    indicators: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pulsed, setPulsed] = useState({
    incidents: false,
    alerts: false,
    assets: false,
    indicators: false,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevTotalsRef = useRef({ incidents: 0, alerts: 0, assets: 0, indicators: 0 });

  // ReactFlow instance refs for auto-fit
  const inlineFlowRef = useRef(null);
  const modalFlowRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        let rows = [];
        let summary = null;

        try {
          const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.WORKSPACE_LOG_METRICS, {
            params: { range },
          });
          rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
          rows = rows.map(normalizeWorkspaceMetric);
          summary = data?.summary || null;
        } catch (innerErr) {
          console.warn("WorkspaceLogMetrics failed, falling back to flow-stats", innerErr);
          const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.DASHBOARD_FLOW_STATS);
          const fsRows = Array.isArray(data?.data?.rules)
            ? data.data.rules
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : [];
          rows = fsRows.map(normalizeRow);
          summary = data?.summary || null;
        }

        if (mounted) {
          const norm = (v) => (v ? v.toString().trim().toLowerCase() : "");
          const targetId = norm(clientId);

          const filtered =
            clientId && targetId
              ? rows.filter((r) =>
                  [r.clientUUID, r.ClientUUID, r.clientId, r.ClientId].map(norm).includes(targetId)
                )
              : rows;

          const finalRows = filtered.length ? filtered : rows;
          setFlowData(finalRows);

          const totalsFromRows = finalRows.reduce(
            (acc, row) => ({
              incidents: acc.incidents + (row.incidents || 0),
              alerts: acc.alerts + (row.alerts || 0),
              assets: acc.assets + (row.assets || 0),
              indicators: acc.indicators + (row.indicators || 0),
            }),
            { incidents: 0, alerts: 0, assets: 0, indicators: 0 }
          );

          const apiSummary = summary || {};
          const combinedTotals = {
            incidents: Number(apiSummary.incidents ?? totalsFromRows.incidents) || 0,
            alerts: Number(apiSummary.alerts ?? totalsFromRows.alerts) || 0,
            assets: Number(apiSummary.assets ?? totalsFromRows.assets) || 0,
            indicators: Number(apiSummary.indicators ?? totalsFromRows.indicators) || 0,
          };

          setSummaryTotals(combinedTotals);
        }
      } catch (err) {
        console.error("Failed to load overview flow stats", err);
        if (mounted) {
          setError(err.response?.data?.message || err.message || "Failed to load flow data.");
          setFlowData([]);
          setSummaryTotals({ incidents: 0, alerts: 0, assets: 0, indicators: 0 });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [range, clientId]);

  const totals = useMemo(() => summaryTotals, [summaryTotals]);

  useEffect(() => {
    const prev = prevTotalsRef.current;
    const changed =
      prev.incidents !== totals.incidents ||
      prev.alerts !== totals.alerts ||
      prev.assets !== totals.assets ||
      prev.indicators !== totals.indicators;

    if (changed) {
      setPulsed({
        incidents: prev.incidents !== totals.incidents,
        alerts: prev.alerts !== totals.alerts,
        assets: prev.assets !== totals.assets,
        indicators: prev.indicators !== totals.indicators,
      });

      setTimeout(() => {
        setPulsed({ incidents: false, alerts: false, assets: false, indicators: false });
      }, PulseWindowMs);
    }

    prevTotalsRef.current = totals;
  }, [totals]);

  const hasFlows = flowData.length > 0;

  // Build node/edge graph with horizontal layout and centered nodes
  const graph = useMemo(() => {
    const nodes = [];
    const edges = [];

    const stats = [
      { key: "incidents", label: "Incidents", value: totals.incidents },
      { key: "alerts", label: "Alerts", value: totals.alerts },
      { key: "assets", label: "Assets", value: totals.assets },
      { key: "indicators", label: "Indicators", value: totals.indicators },
    ];

    const sourceData = flowData.slice(0, 8);
    const workspaceMap = new Map();

    const numSources = sourceData.length;
    const sourceSpacing = 80;
    const totalSourceHeight = (numSources - 1) * sourceSpacing;
    const sourceStartY = Math.max(50, (360 - totalSourceHeight) / 2);

    // Create source nodes (left side)
    sourceData.forEach((flow, idx) => {
      const sourceId = `src-${flow.id}`;
      const workspaceId = `ws-${flow.workspaceName}`;
      const y = sourceStartY + idx * sourceSpacing;

      nodes.push({
        id: sourceId,
        type: "source",
        position: { x: 30, y },
        data: {
          label: flow.tableName,
          details: {
            workspaceName: flow.workspaceName,
            clientName: flow.clientName,
            logCount: flow.logCount,
            periodStart: flow.periodStart,
            periodEnd: flow.periodEnd,
            periodType: flow.periodType,
          },
        },
      });

      if (!workspaceMap.has(workspaceId)) {
        workspaceMap.set(workspaceId, { id: workspaceId, name: flow.workspaceName, sources: [] });
      }
      workspaceMap.get(workspaceId).sources.push(sourceId);
    });

    // Create workspace nodes (middle-left)
    const numWorkspaces = workspaceMap.size;
    const wsSpacing = 100;
    const totalWsHeight = (numWorkspaces - 1) * wsSpacing;
    const wsStartY = Math.max(50, (360 - totalWsHeight) / 2);

    let wsIndex = 0;
    workspaceMap.forEach((ws) => {
      const y = wsStartY + wsIndex * wsSpacing;

      nodes.push({
        id: ws.id,
        type: "workspace",
        position: { x: 280, y },
        data: { label: ws.name },
      });

      ws.sources.forEach((sourceId) => {
        edges.push({
          id: `e-${sourceId}-${ws.id}`,
          source: sourceId,
          target: ws.id,
          type: "bezier",
          animated: true,
          style: { stroke: "rgba(56,189,248,0.6)", strokeWidth: 2, strokeDasharray: "5,5" },
        });
      });

      wsIndex++;
    });

    // Create VorXOC node (center)
    const vorxocY = 360 / 2 - 50;
    nodes.push({
      id: "vorxoc-node",
      type: "vorxoc",
      position: { x: 520, y: vorxocY },
      data: { label: "VorXOC" },
    });

    // Connect workspaces to VorXOC
    workspaceMap.forEach((ws) => {
      edges.push({
        id: `e-${ws.id}-vorxoc`,
        source: ws.id,
        target: "vorxoc-node",
        type: "bezier",
        animated: true,
        style: { stroke: "rgba(34,197,94,0.7)", strokeWidth: 2, strokeDasharray: "5,5" },
      });
    });

    // Create stat nodes (right side)
    const statSpacing = 75;
    const totalStatHeight = (stats.length - 1) * statSpacing;
    const statStartY = Math.max(30, (360 - totalStatHeight) / 2);
    const statColors = {
      incidents: "#38bdf8",
      alerts: "#f97316",
      assets: "#22c55e",
      indicators: "#a855f7",
    };

    stats.forEach((stat, i) => {
      const statId = `stat-${stat.key}`;
      const y = statStartY + i * statSpacing;

      nodes.push({
        id: statId,
        type: "stat",
        position: { x: 750, y },
        data: { label: stat.label, value: stat.value, statKey: stat.key },
      });

      edges.push({
        id: `e-vorxoc-${stat.key}`,
        source: "vorxoc-node",
        target: statId,
        type: "bezier",
        animated: true,
        className: pulsed[stat.key] ? "edge-pulse" : "",
        style: {
          stroke: statColors[stat.key] || "rgba(148,163,184,0.6)",
          strokeWidth: 2.4,
          strokeDasharray: "8,4",
        },
      });
    });

    return { nodes, edges };
  }, [flowData, totals, pulsed]);

  // Auto-fit whenever graph changes or modal opens (fixes “need to click [ ]”)
  useEffect(() => {
    const inst = isFullscreen ? modalFlowRef.current : inlineFlowRef.current;
    if (!inst) return;

    // wait for layout/paint (important when modal opens)
    const t = setTimeout(() => {
      try {
        inst.fitView({ padding: 0.12, duration: 450 });
      } catch {
        // no-op
      }
    }, 60);

    return () => clearTimeout(t);
  }, [graph, isFullscreen]);

  const handleExpand = useCallback(() => setIsFullscreen(true), []);
  const handleCloseFullscreen = useCallback(() => setIsFullscreen(false), []);

  return (
    <Box className="overview-panel overview-panel--graph">
      <div className="overview-panel__header">
        <button className="overview-panel__expand-btn" onClick={handleExpand} title="Expand">
          <Maximize2 size={16} />
        </button>
      </div>

      <Box className="overview-panel__graph">
        {/* Only render the inline flow when modal is closed (prevents two ReactFlow instances at once) */}
        {!isFullscreen && (
          <FlowCanvas
            key="flow-inline"
            nodes={graph.nodes}
            edges={graph.edges}
            onInit={(instance) => {
              inlineFlowRef.current = instance;
              // initial fit
              setTimeout(() => instance.fitView({ padding: 0.12, duration: 350 }), 0);
            }}
          />
        )}
      </Box>

      {!loading && !hasFlows && (
        <Box className="overview-panel__empty">
          <Typography className="overview-panel__muted">
            {error ? `Failed to load: ${error}` : "No sources available"}
          </Typography>
        </Box>
      )}

      {isFullscreen && (
        <CenterModal onClose={handleCloseFullscreen}>
          <div className="overview-flow-modal__canvas">
            <FlowCanvas
              key="flow-modal"
              nodes={graph.nodes}
              edges={graph.edges}
              onInit={(instance) => {
                modalFlowRef.current = instance;
                // initial fit (modal size is different)
                setTimeout(() => instance.fitView({ padding: 0.12, duration: 350 }), 40);
              }}
            />
          </div>
        </CenterModal>
      )}
    </Box>
  );
}
